import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UniverseThemeProvider, useUniverseTheme } from './contexts/UniverseThemeContext';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Home from './pages/Home/Home';
import About from './pages/About/About';
import ForumDetail from './pages/ForumDetail/ForumDetail';
import ThreadDetail from './pages/ThreadDetail/ThreadDetail';
import TestComponent from './pages/TestComponent/TestComponent';
import Characters from './pages/Characters/Characters';
import CharacterForm from './pages/Characters/CharacterForm';
import CharacterProfile from './pages/CharacterProfile/CharacterProfile';
import CharacterTheme from './pages/CharacterTheme/CharacterTheme';
import ThreadTheme from './pages/ThreadTheme/ThreadTheme';
import UserProfile from './pages/UserProfile/UserProfile';
import Players from './pages/Players/Players';
import CharactersCatalog from './pages/CharactersCatalog/CharactersCatalog';
import NpcForm from './pages/Characters/NpcForm';
import Factions from './pages/Factions/Factions';
import FactionForm from './pages/Factions/FactionForm';
import FactionDetail from './pages/Factions/FactionDetail';
import Regulation from './pages/Regulation/Regulation';
import Guide from './pages/Guide/Guide';
import Features from './pages/Features/Features';
import FeatureDetail from './pages/Features/FeatureDetail';
import MemberOfMonth from './pages/MemberOfMonth/MemberOfMonth';
import CharacterOfMonth from './pages/CharacterOfMonth/CharacterOfMonth';
import RpActivities from './pages/RpActivities/RpActivities';
import RpActivityDetail from './pages/RpActivityDetail/RpActivityDetail';
import RpActivityCreate from './pages/RpActivityCreate/RpActivityCreate';
import RpActivityEdit from './pages/RpActivityEdit/RpActivityEdit';
import RpActivityThreadCreate from './pages/RpActivityThreadCreate/RpActivityThreadCreate';
import Profile from './pages/Profile/Profile';
import Dashboard from './pages/Dashboard/Dashboard';
import AnimatorDashboard from './pages/AnimatorDashboard/AnimatorDashboard';
import MessagingList from './pages/Messaging/MessagingList';
import MessagingModeration from './pages/Messaging/MessagingModeration';
import MessagingNew from './pages/Messaging/MessagingNew';
import MessagingThread from './pages/Messaging/MessagingThread';
import MyDrafts from './pages/MyDrafts/MyDrafts';
import Cards from './pages/Components/Cards/Cards';
import Sidebar from './pages/Components/Sidebar/Sidebar';
import ThreadHeader from './pages/Components/ThreadHeader/ThreadHeader';
import CharacterHeader from './pages/Components/CharacterHeader/CharacterHeader';
import ThreadList from './pages/Components/ThreadList/ThreadList';
import ForumDetailTest from './pages/Components/ForumDetail/ForumDetail';
import ForumDetailHeader from './pages/Components/ForumDetailHeader/ForumDetailHeader';
import ThreadFilter from './pages/Components/ThreadFilter/ThreadFilter';
import ThreadPostHRP from './pages/Components/ThreadPostHRP/ThreadPostHRP';
import ThreadPostRP from './pages/Components/ThreadPostRP/ThreadPostRP';
import RpActivityCardComponent from './pages/Components/RpActivityCard/RpActivityCard';
import AiScenes from './pages/AiScenes/AiScenes';
import Components from './pages/Components/Components';
import Loading from './components/Loading/Loading';
import SeoHead from './components/Seo/SeoHead';
import { isPrivateRoute } from './hooks/useSeo';
import { getUniverseForumsPath } from './utils/universePaths';
import './styles/global.css';

const RouteSeoGuard = () => {
  const location = useLocation();

  if (!isPrivateRoute(location.pathname)) {
    return null;
  }

  return (
    <SeoHead
      seo={{
        metaTitle: 'Earth Forum',
        metaDescription: '',
        robotsIndex: false,
      }}
    />
  );
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Loading message="Vérification de l'authentification..." />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const ForumsRedirect = () => {
  const { currentUniverse } = useUniverseTheme();
  return <Navigate to={getUniverseForumsPath(currentUniverse)} replace />;
};

const AppRoutes = () => {
  return (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Home />} />
            <Route path="/qui-sommes-nous" element={<About />} />
            <Route path="/reglement" element={<Regulation />} />
            <Route path="/mode-emploi" element={<Guide />} />
            <Route path="/fonctionnalites" element={<Features />} />
            <Route path="/fonctionnalites/:slug" element={<FeatureDetail />} />
            <Route path="/member-of-month" element={<MemberOfMonth />} />
            <Route path="/member-of-month/:universeSlug" element={<MemberOfMonth />} />
            <Route path="/character-of-month" element={<CharacterOfMonth />} />
            <Route path="/character-of-month/:universeSlug" element={<CharacterOfMonth />} />
            <Route path="/rp-activities" element={<RpActivities />} />
            <Route path="/rp-activities/new" element={<PrivateRoute><RpActivityCreate /></PrivateRoute>} />
            <Route path="/rp-activities/:id" element={<RpActivityDetail />} />
            <Route path="/rp-activities/:id/edit" element={<PrivateRoute><RpActivityEdit /></PrivateRoute>} />
            <Route path="/rp-activities/:id/create-thread" element={<PrivateRoute><RpActivityThreadCreate /></PrivateRoute>} />
          <Route path="/tableau-de-bord" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/espace-animateur" element={<PrivateRoute><AnimatorDashboard /></PrivateRoute>} />
          <Route path="/messagerie" element={<PrivateRoute><MessagingList /></PrivateRoute>} />
          <Route path="/messagerie/nouveau" element={<PrivateRoute><MessagingNew /></PrivateRoute>} />
          <Route path="/messagerie/moderation" element={<PrivateRoute><MessagingModeration /></PrivateRoute>} />
          <Route path="/messagerie/:id" element={<PrivateRoute><MessagingThread /></PrivateRoute>} />
          <Route path="/mes-brouillons" element={<PrivateRoute><MyDrafts /></PrivateRoute>} />
          <Route path="/profil" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/test" element={<TestComponent />} />
            <Route path="/univers/:slug" element={<ForumDetail />} />
            <Route path="/univers/:slug/elseworld/:elseworldSlug" element={<ForumDetail />} />
            <Route path="/forums" element={<ForumsRedirect />} />
            <Route path="/forums/:slug" element={<ForumDetail />} />
            <Route path="/threads/:slug/theme" element={<PrivateRoute><ThreadTheme /></PrivateRoute>} />
            <Route path="/threads/:slug" element={<ThreadDetail />} />
            <Route path="/characters/new" element={<PrivateRoute><CharacterForm /></PrivateRoute>} />
            <Route path="/characters/npc/new" element={<PrivateRoute><NpcForm /></PrivateRoute>} />
            <Route path="/characters/npc/:id/edit" element={<PrivateRoute><NpcForm /></PrivateRoute>} />
            <Route path="/characters/:id/edit" element={<PrivateRoute><CharacterForm /></PrivateRoute>} />
            <Route path="/characters/:id/theme" element={<PrivateRoute><CharacterTheme /></PrivateRoute>} />
            <Route path="/characters/:id" element={<CharacterProfile />} />
            <Route path="/users/:id" element={<UserProfile />} />
            <Route path="/joueurs" element={<Players />} />
            <Route path="/personnages" element={<CharactersCatalog />} />
            <Route path="/characters" element={<PrivateRoute><Characters /></PrivateRoute>} />
            <Route path="/mes-factions" element={<PrivateRoute><Factions mode="mine" /></PrivateRoute>} />
            <Route path="/factions/new" element={<PrivateRoute><FactionForm /></PrivateRoute>} />
            <Route path="/factions/:id/edit" element={<PrivateRoute><FactionForm /></PrivateRoute>} />
            <Route path="/factions/:id" element={<FactionDetail />} />
            <Route path="/factions" element={<Factions mode="browse" />} />
          <Route path="/scenes-ia" element={<PrivateRoute><AiScenes /></PrivateRoute>} />
          <Route path="/scenes-ia/:id" element={<PrivateRoute><AiScenes /></PrivateRoute>} />
            <Route path="/components" element={<Components />} />
            <Route path="/components/cards" element={<Cards />} />
            <Route path="/components/sidebar" element={<Sidebar />} />
            <Route path="/components/threadheader" element={<ThreadHeader />} />
            <Route path="/components/characterheader" element={<CharacterHeader />} />
            <Route path="/components/threadlist" element={<ThreadList />} />
            <Route path="/components/forumdetail" element={<ForumDetailTest />} />
            <Route path="/components/forumdetailheader" element={<ForumDetailHeader />} />
            <Route path="/components/threadfilter" element={<ThreadFilter />} />
            <Route path="/components/threadposthrp" element={<ThreadPostHRP />} />
            <Route path="/components/threadpostrp" element={<ThreadPostRP />} />
            <Route path="/components/rpactivitycard" element={<RpActivityCardComponent />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <UniverseThemeProvider>
        <AuthProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <RouteSeoGuard />
            <AppRoutes />
          </Router>
        </AuthProvider>
      </UniverseThemeProvider>
    </HelmetProvider>
  );
}

export default App;

